import { z } from "zod";

import { IntegrationCatalog } from "@modules/erpIntegrations/types";

export const credentialFieldTypeSchema = z.enum([
  "text",
  "password",
  "url",
  "textarea",
  "number",
  "boolean",
]);

export const credentialSchemaFieldSchema = z.object({
  key: z.string().min(1),
  label: z.string().optional(),
  type: credentialFieldTypeSchema.optional(),
  required: z.boolean().optional(),
});

export const integrationSchema = () => {
  return z.object({
    name: z.string().min(1),
    description: z.string().optional().default(""),
    documentation_url: z
      .string()
      .optional()
      .default("")
      .superRefine((value, ctx) => {
        if (!value) return;
        if (z.string().url().safeParse(value).success) return;
        ctx.addIssue({
          code: z.ZodIssueCode.invalid_string,
          validation: "url",
        });
      }),
    instructions: z.string().optional().default(""),
    auth_endpoint: z
      .union([z.literal(""), z.null(), z.coerce.number().min(1)])
      .optional()
      .transform((value) => (value === "" || value === undefined ? null : value)),
    auth_token_response_key: z.string().optional().default(""),
    active: z.boolean().optional().default(true),
    features: z.array(z.unknown()).optional().default([]),
    credential_schema: z.array(credentialSchemaFieldSchema).optional().default([]),
  });
};

export type IntegrationFormValues = z.infer<ReturnType<typeof integrationSchema>>;

export const configSchema = (integrations: IntegrationCatalog[] = []) => {
  return z
    .object({
      agreement: z.coerce.number().min(1),
      integration: z.coerce.number().min(1),
      active: z.boolean().optional().default(true),
      credentials: z
        .record(z.union([z.string(), z.number(), z.boolean()]))
        .optional()
        .default({}),
      test_document: z.string().optional().default(""),
      access_token: z.string().optional().default(""),
      plan_map_option: z
        .union([z.coerce.number(), z.literal("")])
        .optional()
        .nullable()
        .transform((value) => (value === "" || value === undefined ? null : value)),
    })
    .superRefine((data, ctx) => {
      const integration = integrations.find((item) => item.id === data.integration);
      const schema = integration?.credential_schema ?? [];

      for (const field of schema) {
        if (!field.required) continue;
        const value = data.credentials?.[field.key];
        if (value === undefined || value === null || value === "") {
          ctx.addIssue({
            path: ["credentials", field.key],
            code: z.ZodIssueCode.custom,
          });
        }
      }
    });
};

export type ConfigFormValues = z.infer<ReturnType<typeof configSchema>>;

export const planMapOptionSchema = () => {
  return z.object({
    integration: z.coerce.number().min(1),
    endpoint: z.coerce.number().min(1),
    label: z.string().min(1),
    field: z
      .string()
      .min(1)
      .refine((value) => !/[.[\]]/.test(value), {
        message: 'O campo não pode conter "." ou "[]" — use a chave direta do item (ex.: "contrato").',
      }),
    field_type: z.enum(["int", "str"]),
    is_default: z.boolean().optional().default(false),
    active: z.boolean().optional().default(true),
  });
};

export type PlanMapOptionFormValues = z.infer<ReturnType<typeof planMapOptionSchema>>;

const endpointFieldSchema = z.object({
  key: z.string().trim().min(1),
  label: z.string().optional(),
  required: z.boolean().optional(),
  default: z.string().optional(),
  prefix: z.string().optional(),
  from_access_token: z.boolean().optional(),
});

const endpointFieldListSchema = z.array(endpointFieldSchema).superRefine((items, ctx) => {
  const seen = new Set<string>();

  items.forEach((item, index) => {
    const key = item.key.trim();
    if (!key) return;
    if (seen.has(key)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Chave duplicada nesta lista.",
        path: [index, "key"],
      });
      return;
    }
    seen.add(key);
  });
});

export const endpointSchema = () => {
  return z.object({
    integration: z.coerce.number().min(1),
    slug: z.string().trim().min(1).max(80),
    label: z.string().trim().min(1),
    description: z.string().optional(),
    method: z.enum(["GET", "POST", "PUT", "PATCH", "DELETE"]),
    path: z.string().trim().min(1),
    query: endpointFieldListSchema,
    headers: endpointFieldListSchema,
    body: endpointFieldListSchema,
    body_encoding: z.enum(["", "json", "form", "multipart", "query", "none"]),
    active: z.boolean(),
  });
};

export type EndpointFormValues = z.infer<ReturnType<typeof endpointSchema>>;
